import { lazy, Suspense, useEffect, useState } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { Toaster as SonnerToaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ProtectedRoute from '@/components/ProtectedRoute';
import FreeBanner from '@/components/FreeBanner';
import CookieConsent from '@/components/CookieConsent';
import { hasCookieConsent } from '@/lib/cookieConsent';
import AccessibilityWidget from '@/components/a11y/AccessibilityWidget';
import ScrollToTop from '@/components/ScrollToTop';
import BackToTop from '@/components/BackToTop';
import { MotionConfig, MotionGlobalConfig } from 'framer-motion';
import { useA11yPrefs } from '@/lib/a11yPrefs';

// Public layout is eager (needed for the landing/public routes on first paint).
import PublicLayout from '@/components/layout/PublicLayout';
// Landing (home route) is eager — it's the LCP-critical page. Lazy-loading it put
// the hero <h1> in a separate chunk that arrived at ~3.2s on Slow 4G, which WAS
// the LCP. Eager keeps it in the initial bundle so it renders right after mount.
import Landing from '@/pages/Landing';
// AppLayout + FeatureGate are lazy: they drag in the authenticated-app graph
// (sidebar/header → base44Client → supabase, plan hooks), which must stay OFF
// the public/landing critical path.
const AppLayout = lazy(() => import('@/components/layout/AppLayout'));
const FeatureGate = lazy(() => import('@/components/FeatureGate'));

// Auth pages (lazy — code-split per route for faster initial load)
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));

// Public pages
const Pricing = lazy(() => import('@/pages/Pricing'));
const Features = lazy(() => import('@/pages/Features'));
const About = lazy(() => import('@/pages/About'));
const Contact = lazy(() => import('@/pages/Contact'));
const Terms = lazy(() => import('@/pages/Terms'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Security = lazy(() => import('@/pages/Security'));
const Verify = lazy(() => import('@/pages/Verify'));
const PaymentResult = lazy(() => import('@/pages/PaymentResult'));
const Articles = lazy(() => import('@/pages/Articles'));
const Article = lazy(() => import('@/pages/Article'));
const Accessibility = lazy(() => import('@/pages/Accessibility'));
// דף הדפסה ציבורי — הבוט בוואטסאפ מרנדר אותו דרך דפדפן אמיתי כדי להפיק
// PDF זהה לחלוטין למסמך שיוצא מהאתר (אותו רכיב InvoiceDocument).
const PrintDocument = lazy(() => import('@/pages/PrintDocument'));

// App pages
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Documents = lazy(() => import('@/pages/Documents'));
const NewDocument = lazy(() => import('@/pages/NewDocument'));
const RecurringInvoices = lazy(() => import('@/pages/RecurringInvoices'));
const Reminders = lazy(() => import('@/pages/Reminders'));
const Customers = lazy(() => import('@/pages/Customers'));
const Suppliers = lazy(() => import('@/pages/Suppliers'));
const Products = lazy(() => import('@/pages/Products'));
const Expenses = lazy(() => import('@/pages/Expenses'));
const Reports = lazy(() => import('@/pages/Reports'));
const Settings = lazy(() => import('@/pages/Settings'));
const TaxCallback = lazy(() => import('@/pages/TaxCallback'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const Referrals = lazy(() => import('@/pages/Referrals'));
const WhatsAppGuide = lazy(() => import('@/pages/WhatsAppGuide'));
const WhatsAppLog = lazy(() => import('@/pages/WhatsAppLog'));
const BotAdmin = lazy(() => import('@/pages/BotAdmin'));
// בדיקת נגישות פנימית — lazy כדי ש-axe-core לא ייכנס ל-bundle של משתמשים רגילים
const A11yAudit = lazy(() => import('@/pages/A11yAudit'));

const PageLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
  </div>
);

const AuthenticatedApp = () => {
  const { authError } = useAuth();

  // אין לחסום את כל האפליקציה על isLoadingAuth — זה עיכב גם את דף הנחיתה עד
  // שה-auth (ו-supabase, ~3s) נטענו, וזה היה צוואר הבקבוק של FCP/LCP. מסלולים
  // מוגנים ממתינים ל-auth דרך ProtectedRoute; מסלולים ציבוריים מרונדרים מיד.
  if (authError?.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Auth pages */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* דף הדפסה ציבורי — עצמאי, ללא header/footer/widgets (רינדור נקי ל-PDF) */}
      <Route path="/print-doc" element={<PrintDocument />} />

      {/* Public pages */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/security" element={<Security />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/payment-result" element={<PaymentResult />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<Article />} />
        <Route path="/tax-callback" element={<TaxCallback />} />
      </Route>

      {/* Protected app pages */}
      <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
        <Route element={<AppLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/documents/new" element={<NewDocument />} />
          <Route path="/recurring" element={<FeatureGate feature="recurring"><RecurringInvoices /></FeatureGate>} />
          <Route path="/reminders" element={<FeatureGate feature="reminders"><Reminders /></FeatureGate>} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/products" element={<Products />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/referrals" element={<Referrals />} />
          <Route path="/whatsapp-guide" element={<WhatsAppGuide />} />
          <Route path="/whatsapp-log" element={<WhatsAppLog />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/bot" element={<BotAdmin />} />
          <Route path="/admin/a11y" element={<A11yAudit />} />
        </Route>
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};

// עוטף את העץ ב-MotionConfig כדי שכפתור "עצירת אנימציות" בווידג'ט הנגישות
// יעצור גם אנימציות framer-motion (ולא רק CSS). כשהכפתור כבוי — מכבד את
// העדפת מערכת ההפעלה (prefers-reduced-motion).
// ווידג'טים גלובליים (באנר, חזרה-למעלה, נגישות) — מוסתרים בדף ההדפסה /print-doc
// כדי שלא ייתפסו ב-PDF שמנוע הרינדור מצלם.
function GlobalOverlays() {
  const { pathname } = useLocation();
  // באנר עוגיות (תיקון 13): מוצג עד לבחירה ומחליף את FreeBanner בזמן שהוא פתוח
  // (שניהם צמודי-תחתית — הצגה אחד-אחד מונעת חפיפה). הבחירה נשמרת ב-localStorage
  // ומשודרת כאירוע "cookie-consent" (גם מהכפתור "ניהול העדפות עוגיות" בדף הפרטיות).
  const [cookieDecided, setCookieDecided] = useState(hasCookieConsent);
  useEffect(() => {
    const sync = () => setCookieDecided(hasCookieConsent());
    window.addEventListener('cookie-consent', sync);
    return () => window.removeEventListener('cookie-consent', sync);
  }, []);
  if (pathname === '/print-doc') return null;
  return (
    <>
      {cookieDecided ? <FreeBanner /> : <CookieConsent />}
      <BackToTop />
      <AccessibilityWidget />
    </>
  );
}

function AppShell() {
  const { reduceMotion } = useA11yPrefs();

  // framer-motion מריץ אנימציות ב-JS על style inline, ולכן שכבת ה-CSS של
  // "עצירת אנימציות" (animation-duration/transition-duration) לא נוגעת בהן כלל.
  // גם MotionConfig לבדו לא הספיק: reducedMotion="always" מבטל רק transform/layout
  // ומשאיר את ה-opacity רץ (כל דף נכנס ב-fade), ו-transition מפורש על רכיב גובר
  // על ברירת המחדל של MotionConfig — ויש 20 כאלה בקוד, כולל repeat:Infinity.
  // skipAnimations הוא המתג הגלובלי: הוא כופה duration/delay=0 ברמת ה-motion value,
  // מתחת לכל prop, ולכן עוצר גם אנימציות אינסופיות. מוצב ברינדור (ולא ב-effect)
  // כי effect של הורה רץ אחרי הילדים — האנימציה הראשונה הייתה מספיקה להתחיל.
  MotionGlobalConfig.skipAnimations = reduceMotion;

  return (
    <MotionConfig reducedMotion={reduceMotion ? 'always' : 'user'}>
      <Router>
        <ScrollToTop />
        <a href="#main-content" className="skip-link">דלג לתוכן הראשי</a>
        <AuthenticatedApp />
        <GlobalOverlays />
      </Router>
    </MotionConfig>
  );
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <AppShell />
        <Toaster />
        <SonnerToaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App